import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import WindcreekTabs from 'components/WindcreekTabs';
import WindcreekTab from 'components/WindcreekTab';
import Currency from 'components/Currency';

const CMP_PROFILE = 0;
const OPERA_PROFILE = 1;

const GuestInfoContent = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const GuestInfoContainer = styled.div`
  padding: 0 1rem;
  flex: 0 1 90%;
  overflow-y: scroll;
  font-size: 0.9em;

  @media screen and (max-width: ${prop('theme.breakpoints.extraLarge')}) {
    font-size: 0.8125rem;
    padding: 0 0.5rem;
  }
`;

const StyledWindcreekTabs = styled(WindcreekTabs)`
  padding: 0 2.75rem;
  margin-bottom: 0.5rem;
  flex: 0 1 10%;
`;

const propTypes = {
  guest: PropTypes.shape({
    netTheo: PropTypes.number,
    netTheo365d: PropTypes.number,
    reinvestmentRate: PropTypes.number,
    operaProfile: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      operaId: PropTypes.string,
      customerValue: PropTypes.number,
      address: PropTypes.shape({
        addressLine: PropTypes.string,
        cityName: PropTypes.string,
        stateProv: PropTypes.string,
        countryCode: PropTypes.string,
        postalCode: PropTypes.string
      })
    }),
    patronBookingInfo: PropTypes.shape({
      patronAccountNumber: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      rewardsJoinDate: PropTypes.string,
      homePhone: PropTypes.string,
      mobilePhone: PropTypes.string,
      addressLine1: PropTypes.string,
      addressLine2: PropTypes.string,
      city: PropTypes.string,
      stateProv: PropTypes.string,
      postalCode: PropTypes.string,
      compBalance: PropTypes.number,
      clubState: PropTypes.string
    })
  })
};

const defaultProps = {
  guest: null
};

function GuestInfo({ guest }) {
  const [activeTab, setActiveTab] = useState(0);
  const { operaProfile, patronBookingInfo } = guest;
  const NOT_FOUND = 'not found';

  if (!guest) {
    return null;
  }

  const guestInfo =
    activeTab === OPERA_PROFILE
      ? {
          firstName: operaProfile.firstName,
          lastName: operaProfile.lastName,
          email: operaProfile.email,
          phone: operaProfile.phone,
          address: operaProfile.address && operaProfile.address.addressLine,
          city: operaProfile.address && operaProfile.address.cityName,
          state: operaProfile.address && operaProfile.address.stateProv,
          postalCode:
            operaProfile.address &&
            operaProfile.address.postalCode &&
            operaProfile.address.postalCode.replace(/\D*/g, '')
        }
      : {
          firstName: patronBookingInfo.firstName,
          lastName: patronBookingInfo.lastName,
          email: patronBookingInfo.email,
          phone: patronBookingInfo.mobilePhone || patronBookingInfo.homePhone,
          address:
            patronBookingInfo.addressLine1 +
            (patronBookingInfo.addressLine2
              ? ` ${patronBookingInfo.addressLine2}`
              : ''),
          city: patronBookingInfo.city,
          state: patronBookingInfo.stateProv,
          postalCode: patronBookingInfo.postalCode
        };

  return (
    <GuestInfoContent>
      <StyledWindcreekTabs>
        <WindcreekTab
          className={activeTab === CMP_PROFILE ? 'active' : ''}
          onClick={() => setActiveTab(CMP_PROFILE)}
        >
          CMP
        </WindcreekTab>
        <WindcreekTab
          className={activeTab === OPERA_PROFILE ? 'active' : ''}
          onClick={() => setActiveTab(OPERA_PROFILE)}
        >
          Opera
        </WindcreekTab>
      </StyledWindcreekTabs>
      <GuestInfoContainer>
        {(guestInfo.firstName && guestInfo.firstName !== NOT_FOUND) ||
        (guestInfo.lastName && guestInfo.lastName !== NOT_FOUND)
          ? guestInfo.firstName + ' ' + guestInfo.lastName
          : 'FirstName/LastName Not Found'}
        <br />
        {guestInfo.address && guestInfo.address !== NOT_FOUND
          ? guestInfo.address
          : 'Address Not Found'}
        <br />
        {(guestInfo.city && guestInfo.city !== NOT_FOUND) ||
        (guestInfo.state && guestInfo.state !== NOT_FOUND) ||
        (guestInfo.postalCode && guestInfo.postalCode !== NOT_FOUND)
          ? guestInfo.city + ' ' + guestInfo.state + ' ' + guestInfo.postalCode
          : 'City/State/PostalCode Not Found'}
        <br />
        {guestInfo.email && guestInfo.email !== NOT_FOUND
          ? guestInfo.email
          : 'Email Address Not Found'}
        <br />
        {guestInfo.phone && guestInfo.phone !== NOT_FOUND
          ? guestInfo.phone
          : 'Phone Number Not Found'}
        <br />
        Rainmaker Net ADT:{' '}
        {operaProfile.customerValue ? (
          <Currency
            value={operaProfile.customerValue * 100}
            minimumFractionDigits={0}
            maximumFractionDigits={2}
          />
        ) : (
          'Not Found'
        )}
        <br />
        NetTheo:{' '}
        {guest.netTheo ? (
          <>
            <Currency
              value={guest.netTheo * 100}
              minimumFractionDigits={0}
              maximumFractionDigits={2}
            />
            {' (118d)'}
          </>
        ) : (
          'Not Found (118d)'
        )}
        {', '}
        {guest.netTheo365d ? (
          <>
            <Currency
              value={guest.netTheo365d * 100}
              minimumFractionDigits={0}
              maximumFractionDigits={2}
            />
            {' (365d)'}
          </>
        ) : (
          'Not Found (365d)'
        )}
        <br />
        Reinvestment Rate (118d):{' '}
        {guest.reinvestmentRate
          ? `${(guest.reinvestmentRate * 100).toFixed(2)}%`
          : 'Not Found'}
        <br />
        Comp Balance:{' '}
        {patronBookingInfo.compBalance ? (
          <Currency
            value={patronBookingInfo.compBalance * 100}
            minimumFractionDigits={0}
            maximumFractionDigits={2}
          />
        ) : (
          'Not Found'
        )}
        {patronBookingInfo.clubState && (
          <>
            <br />
            Card Level: {patronBookingInfo.clubState}
          </>
        )}
      </GuestInfoContainer>
    </GuestInfoContent>
  );
}

GuestInfo.propTypes = propTypes;
GuestInfo.defaultProps = defaultProps;

export default GuestInfo;
